<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { isCompleteUsPhone } from '../../helpers/usPhone';

/**
 * The portal's footer (XN-4555): whose portal this is, and how to reach them
 * outside it. The agency's, not ours, so there is no XtendLive mark. Missing
 * details are left out; the hub name always stays. Laid out after the
 * prototype's portalFooter (build 12 Oct 17:52, XN-4734): the agency's mark,
 * how to reach the hub, and their broker; never ours.
 */
const props = defineProps({
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  phone: { type: String, default: '' },
  logo: { type: String, default: '' },
  // undefined = not known (hide the group); null = no broker (XN-4734).
  broker: { type: Object, default: undefined },
  year: { type: Number, default: () => new Date().getFullYear() },
});

// An unnamed broker gets no name line, not "Your broker / Your broker".
const contact = computed(() => (props.broker
  ? { label: 'Your broker', name: props.broker.name }
  : { label: 'Your contact', name: props.name }));

const phoneText = computed(() => (isCompleteUsPhone(props.phone) ? props.phone.trim() : ''));
const tel = computed(() => `tel:${phoneText.value.startsWith('+') ? '+' : ''}${phoneText.value.replace(/\D/g, '')}`);
</script>

<template>
  <footer class="portal-foot" data-testid="portal-footer">
    <div class="portal-foot__in">
      <div class="portal-foot__top">
        <div class="portal-foot__brand">
          <img v-if="logo" :src="logo" :alt="name" class="portal-foot__logo" />
          <b v-else class="portal-foot__name">{{ name }}</b>
          <p>Your policies, your renewal dates and a direct line to your broker.</p>
        </div>
        <div class="portal-foot__cols">
          <div v-if="email || phoneText" class="portal-foot__touch">
            <b>Get in touch</b>
            <a v-if="email" class="portal-foot__link" :href="`mailto:${email}`">{{ email }}</a>
            <a v-if="phoneText" class="portal-foot__link" :href="tel">{{ phoneText }}</a>
          </div>
          <div v-if="broker !== undefined" class="portal-foot__grp" data-testid="footer-broker">
            <b>{{ contact.label }}</b>
            <span v-if="contact.name" class="portal-foot__text">{{ contact.name }}</span>
            <RouterLink to="/hub/messages" class="portal-foot__link">Send a message</RouterLink>
          </div>
        </div>
      </div>
      <p class="portal-foot__copy">© {{ year }} {{ name }}</p>
    </div>
  </footer>
</template>

<style scoped>
/* Mirrors the header's hairline; the content sits in the page's 940px column. */
.portal-foot { border-top: 1px solid var(--xl-color-border); }
.portal-foot__in {
  max-width: 940px; margin: 0 auto; padding: 24px 26px 40px;
  font-size: 13.5px; color: var(--xl-color-outline);
}
.portal-foot__top { display: flex; justify-content: space-between; gap: 40px; flex-wrap: wrap; }
.portal-foot__brand { max-width: 300px; }
.portal-foot__logo { height: 26px; width: auto; max-width: 180px; object-fit: contain; display: block; }
.portal-foot__name { font-size: 16px; font-weight: 700; color: var(--xl-color-primary); letter-spacing: .01em; overflow-wrap: anywhere; }
.portal-foot__brand p { margin: 12px 0 0; font-size: 13.5px; line-height: 1.55; color: var(--xl-color-outline); }
.portal-foot__cols { display: flex; gap: 56px; flex-wrap: wrap; }
.portal-foot__touch, .portal-foot__grp { display: grid; gap: 4px; align-content: start; }
.portal-foot__touch b, .portal-foot__grp b { font-size: 14px; font-weight: 600; color: var(--xl-color-primary-text); margin-bottom: 2px; }
.portal-foot__copy { margin: 28px 0 0; text-align: center; font-size: 12.5px; color: var(--xl-color-outline); }
.portal-foot__link, .portal-foot__text { overflow-wrap: anywhere; }
.portal-foot__link { color: var(--xl-color-primary); text-decoration: none; }
.portal-foot__link:hover { text-decoration: underline; }
.portal-foot__link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; border-radius: 4px; }

@media (max-width: 480px) {
  .portal-foot__in { padding: 20px 16px 32px; }
}
</style>
